import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { Loader2, Megaphone, Trash2 } from "lucide-react";
import KnowledgeTab from "@/pages/KnowledgeTab";
import WidgetTab from "@/pages/WidgetTab";

const ROLES = ["customer", "agent", "admin"];

export default function Admin() {
  const { user } = useAuth();
  if (user?.role !== "admin") {
    return <div className="text-sm text-muted-foreground">Admin only.</div>;
  }
  return (
    <div className="space-y-6">
      <header>
        <div className="font-mono text-[10px] tracking-widest uppercase text-primary mb-2">// Admin</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Control room</h1>
      </header>
      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users & roles</TabsTrigger>
          <TabsTrigger value="announcements" data-testid="admin-tab-announcements">Announcements</TabsTrigger>
          <TabsTrigger value="knowledge" data-testid="admin-tab-knowledge">Knowledge base</TabsTrigger>
          <TabsTrigger value="widget" data-testid="admin-tab-widget">Widget</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="pt-6"><UsersTab /></TabsContent>
        <TabsContent value="announcements" className="pt-6"><AnnouncementsTab /></TabsContent>
        <TabsContent value="knowledge" className="pt-6"><KnowledgeTab /></TabsContent>
        <TabsContent value="widget" className="pt-6"><WidgetTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const load = async () => { const { data } = await api.get("/admin/users"); setUsers(data); };
  useEffect(() => { load(); }, []);
  const changeRole = async (uid, role) => {
    try { await api.patch(`/admin/users/${uid}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };
  return (
    <div className="rounded-lg border border-border/60 bg-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-secondary/40 border-b border-border/60">
          <tr>
            <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Name</th>
            <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Email</th>
            <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Provider</th>
            <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Role</th>
            <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Joined</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} className="border-b border-border/40">
              <td className="px-4 py-3">{u.name}</td>
              <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
              <td className="px-4 py-3 font-mono text-xs uppercase tracking-widest text-muted-foreground">{u.provider || "local"}</td>
              <td className="px-4 py-3">
                <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                  <SelectTrigger className="h-8 w-[130px]" data-testid={`role-${u.user_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground">{u.created_at ? format(new Date(u.created_at), "PP") : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [variant, setVariant] = useState("info");
  const [audience, setAudience] = useState("everyone");
  const [saving, setSaving] = useState(false);

  const load = async () => { const { data } = await api.get("/announcements"); setItems(data); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/announcements", { title, message, variant, audience, dismissible: true });
      setTitle(""); setMessage("");
      toast.success("Announcement published");
      load();
    } catch (e2) { toast.error(extractError(e2)); }
    finally { setSaving(false); }
  };

  const toggle = async (a) => { await api.patch(`/announcements/${a.announcement_id}`, { active: !a.active }); load(); };
  const remove = async (a) => { await api.delete(`/announcements/${a.announcement_id}`); load(); };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <form onSubmit={create} className="p-5 rounded-lg border border-border/60 bg-card space-y-3">
        <div className="font-display font-bold text-lg flex items-center gap-2"><Megaphone className="h-4 w-4 text-primary" /> New announcement</div>
        <div className="space-y-1.5">
          <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Title</Label>
          <Input data-testid="ann-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Message</Label>
          <Textarea data-testid="ann-message" value={message} onChange={(e) => setMessage(e.target.value)} required rows={4} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Variant</Label>
            <Select value={variant} onValueChange={setVariant}>
              <SelectTrigger data-testid="ann-variant"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Audience</Label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="ann-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="everyone">Everyone</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="customers">Customers</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button data-testid="ann-submit" disabled={saving} className="w-full gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish"}</Button>
      </form>

      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.announcement_id} className="p-4 rounded-lg border border-border/60 bg-card">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-sm">{a.title}</div>
                <div className="text-xs text-muted-foreground">{a.message}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-2">
                  {a.variant} · {a.audience} · {a.active ? "active" : "inactive"}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => toggle(a)}>{a.active ? "Disable" : "Enable"}</Button>
                <Button size="icon" variant="ghost" onClick={() => remove(a)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground">No announcements yet.</div>}
      </div>
    </div>
  );
}
